import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-yellow-500 hover:text-yellow-400 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-bold">Back to Backlog Killer</span>
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-4">
            <span className="bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">
              TERMS
            </span>
            {' '}
            <span className="text-white">OF SERVICE</span>
          </h1>
          <p className="text-gray-400 text-lg">Last Updated: January 2025</p>
        </div>

        {/* Content */}
        <div className="space-y-8 text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing and using Backlog Killer ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Description of Service</h2>
            <p>
              Backlog Killer is a game discovery platform that helps users find video games to play through a randomized recommendation system. The Service uses the IGDB (Internet Game Database) API to provide game information and recommendations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Affiliate Disclosure</h2>
            <p className="mb-4">
              Backlog Killer participates in affiliate marketing programs, including but not limited to the eBay Partner Network. This means we may earn a commission when you click on certain links and make a purchase.
            </p>
            <p>
              These affiliate relationships do not influence our game recommendations, which are generated randomly from the IGDB database. We are committed to providing unbiased game suggestions regardless of potential earnings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. User Conduct</h2>
            <p className="mb-3">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Use the Service for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Collect or harvest any personally identifiable information from the Service</li>
              <li>Use automated systems or software to extract data from the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Intellectual Property</h2>
            <p className="mb-4">
              Game information, images, and data displayed on Backlog Killer are sourced from IGDB and are subject to their respective copyrights and trademarks. All game-related content remains the property of their respective owners.
            </p>
            <p>
              The Backlog Killer website design, layout, and original content are © 2025 Backlog Killer. All rights reserved.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Third-Party Links</h2>
            <p>
              The Service contains links to third-party websites and services, including eBay and game retailers. We are not responsible for the content, policies, or practices of any third-party websites. Your use of third-party websites is at your own risk and subject to their terms and conditions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Disclaimer of Warranties</h2>
            <p className="mb-3">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Accuracy or completeness of game information</li>
              <li>Availability or uninterrupted access to the Service</li>
              <li>Fitness for a particular purpose</li>
              <li>Non-infringement of third-party rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Limitation of Liability</h2>
            <p>
              In no event shall Backlog Killer, its operators, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Service, even if we have been advised of the possibility of such damages.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Data Collection and Privacy</h2>
            <p>
              We do not collect personal information from users. The Service may use cookies and local storage for functionality purposes. Your use of the Service is also governed by our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to the Service. Your continued use of the Service after any changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Age Restrictions</h2>
            <p>
              The Service is intended for users aged 13 and older. If you are under 13, please do not use this Service. If you are between 13 and 18, you should review these terms with your parent or guardian.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with applicable laws, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13. Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at <a href="mailto:contact@backlogkiller.com" className="text-yellow-500 hover:text-yellow-400">contact@backlogkiller.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">14. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">15. Entire Agreement</h2>
            <p>
              These Terms constitute the entire agreement between you and Backlog Killer regarding the use of the Service, superseding any prior agreements between you and Backlog Killer relating to your use of the Service.
            </p>
          </section>
        </div>

        {/* Back to top button */}
        <div className="mt-12 text-center">
          <Link 
            to="/" 
            className="inline-block bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-gray-950 font-black py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Back to Game Discovery
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Terms;
